// Maps raw /2fa/auth/fcm/list rows to the AdminHeader notification panel
// shape. Pure functions — easy to unit-test in isolation if we ever do.
//
// The raw rows are de_notify.notify records (DeNotify.Notify): they carry
// `objectType` (e.g. "RETURN" | "ORDER") and `objectId` (the entity PK — for
// returns this IS the returnRequestId), which the buyer notifications inbox
// (screens-buyer-app §22) uses to deep-link straight into the detail page.

import { i18n } from "@dehub/theme"
import dayjs from "dayjs"
import { notifTarget } from "./notif-target"

export type NotifRow = {
  id: string
  title?: string
  subject?: string
  body?: string
  message?: string
  type?: string
  category?: string
  // de_notify.notify structured fields (the deep-link source).
  objectType?: string | null
  objectId?: string | null
  data?: string | null
  createdAt?: string
  seen?: boolean
  actionLabel?: string
}

// Kept in lock-step with @dehub/theme's NotifKind (the header bell consumes
// MappedNotif as NotifItem). Return rows are flagged via `isReturn` + the
// return-* fields below rather than a new kind value, so the bell type holds.
export type NotifKind = "order" | "payment" | "contract" | "partner" | "alert"

// §22 returns notification context — a coarse intent derived from the title/body
// so the inbox can render the right inline action. All actions deep-link to the
// return detail page (by objectId when present, else the hub search fallback).
export type ReturnNotifKind =
  | "PROPOSED" // action-required: staff proposed new terms
  | "REFUND" // refund transferred
  | "REPLACEMENT" // replacement order ready
  | "COUPON" // discount coupon created
  | "STALE" // D20 inactivity warning
  | "PICKUP_SLA" // BUYER_SHIPS pickup-SLA reminder
  | "TRANSIT_EXCEPTION" // D13 in-transit exception
  | "GENERIC" // any other return notification

export interface MappedNotif {
  id: string
  kind: NotifKind
  title: string
  body: string
  time: string
  ts: string
  group: "today" | "week"
  unread: boolean
  action?: string
  objectType?: string | null
  objectId?: string | null
  // §22 — return deep-link context (only set when isReturn).
  isReturn?: boolean
  returnId?: string | null
  returnNumber?: string | null
  couponCode?: string | null
  returnKind?: ReturnNotifKind
}

export function mapNotif(n: NotifRow): MappedNotif {
  const created = n.createdAt ? new Date(n.createdAt) : new Date()
  const ageDays = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24)
  const group: "today" | "week" = ageDays < 1 ? "today" : "week"
  const title = n.title ?? n.subject ?? i18n.t("layout:notif.defaultTitle")
  const body = n.body ?? n.message ?? ""
  const source = n.objectType ?? n.type ?? n.category ?? `${title} ${body}`
  const isReturn = isReturnNotif(source, `${title} ${body}`)
  const base: MappedNotif = {
    id: n.id,
    // The bell expects a theme NotifKind — return rows render as "alert" there.
    kind: isReturn ? "alert" : detectKind(source),
    title,
    body,
    time: relativeTime(created),
    ts: dayjs(created).format("YYYY-MM-DD HH:mm"),
    group,
    unread: !n.seen,
    action: n.actionLabel,
    objectType: n.objectType ?? null,
    objectId: n.objectId ?? extractObjectId(n.data),
  }
  if (!isReturn) return base
  // de_notify.notify.objectId === returnRequestId for RETURN rows → real deep-link.
  const objectId =
    n.objectType?.toUpperCase() === "RETURN" && n.objectId ? n.objectId : extractObjectId(n.data)
  return {
    ...base,
    isReturn: true,
    returnId: objectId ?? null,
    returnNumber: extractReturnNumber(`${title} ${body}`),
    couponCode: extractCouponCode(`${title} ${body}`),
    returnKind: detectReturnKind(`${title} ${body}`),
  }
}

export function notifHref(n: MappedNotif, isBuyer: boolean): string | null {
  if (n.isReturn) {
    if (isBuyer) {
      if (n.returnId) return `/buyer-app/my-returns/${n.returnId}`
      if (n.returnNumber) return `/buyer-app/my-returns?search=${encodeURIComponent(n.returnNumber)}`
      return "/buyer-app/my-returns"
    }
    if (n.returnId) return `/returns/${n.returnId}`
    if (n.returnNumber) return `/returns?search=${encodeURIComponent(n.returnNumber)}`
    return "/returns"
  }

  const type = String(n.objectType ?? "").toUpperCase()
  const id = n.objectId ?? null

  switch (type) {
    case "ORDER":
      if (isBuyer) return id ? `/buyer-app/orders/${id}` : "/buyer-sales"
      return id ? `/supplier-purchase/detail/${id}` : "/supplier-purchase"
    case "DELIVERY_ROUTE":
      if (isBuyer) return null
      return id ? `/delivery-routes/${id}` : "/delivery-routes"
    case "DELIVERY_NOTE":
      return isBuyer ? null : "/supplier-delivery"
    default:
      return null
  }
}

/** Can the row open anything for this portal? Rows without a target are marked read only. */
export function notifHasTarget(n: MappedNotif, isBuyer: boolean): boolean {
  return !!notifHref(n, isBuyer) || !!notifTarget(n.objectType, n.objectId, isBuyer)
}

// B50: object types whose notification asks the buyer to act. The de_notify `type` column is the
// delivery channel (TOPIC/EMAIL/...), so the object type is the BE field that carries intent.
const ACTION_OBJECT_TYPES = ["QUOTATION", "INVOICE"]

export function isActionRequired(n: MappedNotif): boolean {
  if (!n.unread) return false
  if (n.isReturn) return n.returnKind === "PROPOSED"
  return ACTION_OBJECT_TYPES.includes(String(n.objectType ?? "").toUpperCase())
}

// §22 — is this a return notification? objectType="RETURN", a RET- reference, or
// the Mongolian "буцаа" stem in the type/title/body.
export function isReturnNotif(source: string, text: string): boolean {
  const s = String(source || "").toLowerCase()
  if (s === "return" || s.includes("return") || s.includes("буцаа")) return true
  return /\bret-/i.test(text)
}

export function detectKind(value?: string): NotifKind {
  const v = String(value || "").toLowerCase()
  if (v.includes("order")) return "order"
  if (v.includes("payment") || v.includes("төлбөр")) return "payment"
  if (v.includes("contract") || v.includes("гэрээ")) return "contract"
  if (v.includes("partner") || v.includes("партнер")) return "partner"
  return "alert"
}

// §22 — classify a return notification into its inline-action intent from the
// Mongolian copy (the de_notify rows carry no explicit sub-type).
export function detectReturnKind(text: string): ReturnNotifKind {
  const v = text.toLowerCase()
  if (v.includes("санал болс") || v.includes("нөхцөл") || v.includes("proposed")) return "PROPOSED"
  if (v.includes("шилжүүлэг") || v.includes("refund")) return "REFUND"
  if (v.includes("replacement") || v.includes("орлуул")) return "REPLACEMENT"
  if (v.includes("купон") || v.includes("coupon")) return "COUPON"
  if (v.includes("идэвхгүй") || v.includes("автомат цуцл") || v.includes("stale")) return "STALE"
  if (v.includes("sla") || v.includes("pickup") || v.includes("илгээх хугацаа")) return "PICKUP_SLA"
  if (v.includes("exception") || v.includes("замд асуудал") || v.includes("transit"))
    return "TRANSIT_EXCEPTION"
  return "GENERIC"
}

// RET-2026-0439 / RET-DC-2026-A4F2 (coupon) — the buyer-facing reference number.
export function extractReturnNumber(text: string): string | null {
  const m = text.match(/RET-(?:DC-)?[A-Z0-9-]+/i)
  return m ? m[0] : null
}

// RET-DC-… coupon code (§19); distinct from the RET- request number.
export function extractCouponCode(text: string): string | null {
  const m = text.match(/RET-DC-[A-Z0-9-]+/i)
  return m ? m[0] : null
}

// Fallback objectId extraction from the JSON `data` blob ({ data: { objectId } }).
function extractObjectId(data?: string | null): string | null {
  if (!data) return null
  try {
    const parsed = JSON.parse(data) as { data?: { objectId?: string } }
    return parsed?.data?.objectId ?? null
  } catch {
    return null
  }
}

export function relativeTime(d: Date): string {
  const sec = Math.floor((Date.now() - d.getTime()) / 1000)
  if (sec < 60) return i18n.t("layout:notif.timeAgo.justNow")
  const min = Math.floor(sec / 60)
  if (min < 60) return i18n.t("layout:notif.timeAgo.minutes", { count: min })
  const hr = Math.floor(min / 60)
  if (hr < 24) return i18n.t("layout:notif.timeAgo.hours", { count: hr })
  const day = Math.floor(hr / 24)
  if (day === 1) return i18n.t("layout:notif.timeAgo.yesterday")
  if (day < 7) return i18n.t("layout:notif.timeAgo.days", { count: day })
  return dayjs(d).format("YYYY-MM-DD")
}
